/**
 * Ngày nghỉ của một KTV (F06 — tuần 1 phiên bản mới, T7), nằm trong tờ sửa nhân viên.
 *
 * Nguyên ngày, "từ ngày … đến ngày …". Từ STT 3.4 (ADR-092) cho nghỉ LUÔN LƯU: vướng lịch đã có thì máy chủ vẫn trả
 * 201 kèm danh sách lịch vướng; hiện ngay ở đây (cảnh báo, không phải lỗi) để chủ tiệm biết lịch nào còn phải chuyển
 * người — không lặng lẽ để khách tới mà thợ nghỉ. Mỗi dòng ngày nghỉ còn lịch vướng ghi "còn n lịch cần chuyển".
 */
import { CalendarOff, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { ApiFailure, cauHinh, type LichVuongNghi, type NgayNghi } from './api.ts'
import { lucViet, ngayViet, ONgay } from './GoiVi.tsx'
import { laLoiCaTiem } from './nghi-dot-xuat.ts'
import { T } from './text/vi.ts'
import { ThongBao } from './ui.tsx'

export function MucNgayNghi({
  staffId,
  onLoi,
  onMoChiNhanh,
}: {
  staffId: string
  onLoi: (e: unknown) => void
  /** v0.22.3 B1: lối sang Cấu hình → Chi nhánh (Ngày tiệm nghỉ) khi người này là người nhận khách cuối cùng. */
  onMoChiNhanh?: () => void
}) {
  const [ds, setDs] = useState<NgayNghi[] | null>(null)
  /** Câu của máy chủ khi từ chối vì người này là người nhận khách cuối cùng (không phải lỗi — việc cần làm khác). */
  const [cauCuoi, setCauCuoi] = useState<string | null>(null)
  const [tu, setTu] = useState('')
  const [den, setDen] = useState('')
  const [lyDo, setLyDo] = useState('')
  const [vuong, setVuong] = useState<LichVuongNghi[]>([])
  const [dang, setDang] = useState(false)
  /** Tăng sau mỗi lần lưu để ô ngày gõ-tay dựng lại (ô tự giữ chữ nên không tự xoá khi `giaTri` về rỗng). */
  const [lan, setLan] = useState(0)

  const nap = useCallback(() => {
    void cauHinh
      .ngayNghi(staffId)
      .then((r) => setDs(r.ngayNghi))
      .catch(onLoi)
  }, [staffId, onLoi])
  useEffect(nap, [nap])

  async function choNghi() {
    if (tu === '') return
    setDang(true)
    setVuong([])
    setCauCuoi(null)
    try {
      const r = await cauHinh.choNghi(staffId, {
        tuNgay: tu,
        denNgay: den === '' ? tu : den,
        ...(lyDo.trim() === '' ? {} : { lyDo: lyDo.trim() }),
      })
      // Đã lưu. Còn lịch vướng thì nói rõ (cảnh báo) để chuyển người ở màn Lịch của ngày đó.
      setVuong(r.lichVuong)
      setTu('')
      setDen('')
      setLyDo('')
      setLan((n) => n + 1)
      nap()
    } catch (e) {
      if (laLoiCaTiem(e) && e instanceof ApiFailure) setCauCuoi(e.messageVi)
      else onLoi(e)
    } finally {
      setDang(false)
    }
  }

  async function huy(id: string) {
    try {
      await cauHinh.huyNghi(id)
      nap()
    } catch (e) {
      onLoi(e)
    }
  }

  return (
    <div className="muc-to">
      <h3>{T.offTitle}</h3>
      {ds !== null && ds.length > 0 && (
        <ul className="ds-nghi">
          {ds.map((n) => (
            <li key={n.id}>
              <CalendarOff size={18} />
              <span>
                <b>
                  {n.tuNgay === n.denNgay
                    ? ngayViet(n.tuNgay)
                    : `${ngayViet(n.tuNgay)} – ${ngayViet(n.denNgay)}`}
                </b>
                {n.lyDo !== null && <span className="chu-phu"> · {n.lyDo}</span>}
                {(n.soLichVuong ?? 0) > 0 && (
                  <span className="chu-canh-bao"> · {T.offStillBusy(n.soLichVuong ?? 0)}</span>
                )}
              </span>
              <button
                type="button"
                className="nut nut-nhat nut-icon"
                aria-label={T.offCancel}
                onClick={() => void huy(n.id)}
              >
                <X size={20} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="hang-nghi">
        <div className="truong">
          <label className="nhan" htmlFor={`nghi-tu-${staffId}`}>
            {T.offFrom}
          </label>
          <ONgay
            key={`tu-${lan}`}
            id={`nghi-tu-${staffId}`}
            giaTri={tu}
            onDoi={setTu}
            goiYKhiDung={() => ''}
          />
        </div>
        <div className="truong">
          <label className="nhan" htmlFor={`nghi-den-${staffId}`}>
            {T.offTo}
          </label>
          <ONgay
            key={`den-${lan}`}
            id={`nghi-den-${staffId}`}
            giaTri={den}
            onDoi={setDen}
            goiY={T.offToHint}
            goiYKhiDung={() => ''}
          />
        </div>
      </div>
      <div className="truong">
        <label className="nhan" htmlFor={`nghi-ly-do-${staffId}`}>
          {T.offReason}
        </label>
        <input
          id={`nghi-ly-do-${staffId}`}
          className="o-nhap"
          value={lyDo}
          maxLength={200}
          placeholder={T.offReasonExample}
          onChange={(e) => setLyDo(e.target.value)}
        />
      </div>
      {cauCuoi !== null && (
        <ThongBao
          loai="canh-bao"
          {...(onMoChiNhanh === undefined
            ? {}
            : {
                hanhDong: (
                  <button type="button" className="nut nut-vien nut-48" onClick={onMoChiNhanh}>
                    {T.offDaysGoClose}
                  </button>
                ),
              })}
        >
          {cauCuoi}
        </ThongBao>
      )}
      {vuong.length > 0 && (
        <ThongBao loai="canh-bao">
          {T.offSavedBusy}
          <ul className="ds-vuong">
            {vuong.map((v) => (
              <li key={v.code}>
                {lucViet(v.batDau)}&nbsp;·&nbsp;{giuChuDau(v.khach ?? T.walkInName)} · {v.code}
              </li>
            ))}
          </ul>
        </ThongBao>
      )}
      <button
        type="button"
        className="nut nut-vien"
        disabled={tu === '' || dang}
        onClick={() => void choNghi()}
      >
        <CalendarOff size={18} />
        {T.offAdd}
      </button>
    </div>
  )
}

/** "Chị Lan" không bị bẻ giữa "Chị" và "Lan" (từ đầu ≤ 3 chữ dính với từ sau bằng NBSP) — nút lịch vướng ở 360px. */
export function giuChuDau(ten: string): string {
  return ten.replace(/^(\S{1,3})\s+/u, '$1\u00A0')
}

/**
 * BK-2 (ADR-096): cảnh báo "đã lưu nhưng còn lịch" sau khi đổi ca / cho nghỉ việc / đổi giờ mở cửa. Mỗi lịch là MỘT
 * nút (≥ 48px): bấm = mở màn Lịch đúng ngày + chi nhánh và mở luôn tờ "Xử lý". Máy chủ chỉ trả tối đa 50 dòng;
 * phần còn lại ghi "và n lịch nữa" (dải đỏ màn Lịch vẫn đếm đủ). Chỉ hiển thị — không tự đổi người hay huỷ lịch.
 */
export function CanhBaoLichVuong({
  loiDau,
  ds,
  tong,
  onMo,
  hienMa = true,
}: {
  loiDau: string
  ds: LichVuongNghi[]
  tong: number
  /** `false`: bỏ mã lịch khỏi dòng (thẻ "Ngày tiệm nghỉ" — chủ tiệm nhận ra khách bằng giờ + tên, mã chỉ làm rối). */
  hienMa?: boolean
  /** Có thể `undefined` (không đủ quyền xem Lịch): dòng chỉ là chữ, không phải nút. */
  onMo?: (v: LichVuongNghi) => void
}) {
  if (ds.length === 0) return null
  const them = Math.max(0, tong - ds.length)
  return (
    <ThongBao loai="canh-bao">
      {loiDau}
      <ul className={onMo === undefined ? 'ds-vuong' : 'ds-vuong ds-vuong-nut'}>
        {ds.map((v) => (
          <li key={v.appointmentId}>
            {onMo === undefined ? (
              <span>
                {lucViet(v.batDau)}&nbsp;·&nbsp;{giuChuDau(v.khach ?? T.walkInName)}
                {hienMa ? ` · ${v.code}` : ''}
              </span>
            ) : (
              <button
                type="button"
                className="nut nut-vien nut-lon nut-rong"
                onClick={() => onMo(v)}
              >
                {lucViet(v.batDau)}&nbsp;·&nbsp;{giuChuDau(v.khach ?? T.walkInName)}
                {hienMa ? ` · ${v.code}` : ''}
              </button>
            )}
          </li>
        ))}
      </ul>
      {them > 0 && <p className="chu-phu">{T.busyMore(them)}</p>}
    </ThongBao>
  )
}
